import {
  AccountsLinkDetails,
  accountsLinkService,
} from "@/app/services/generated/accountslinkService";
import {
  TenantDepositSettings,
  bottleDepositSettingsService,
} from "@/app/services/generated/bottledepositsettingsService";
import {
  CompanySettings,
  companySettingsService,
} from "@/app/services/generated/companysettingsService";
import {
  DeliveryNotificationSettings,
  deliveryNotificationService,
} from "@/app/services/generated/deliverynotificationService";
import {
  KegstarSettings,
  kegstarLinkService,
} from "@/app/services/generated/kegstarlinkService";
import type {
  DateAndTimeConfiguration,
  CurrencyConfiguration,
  LanguageAndCultureConfiguration,
  NumberConfiguration,
} from "@/app/services/generated/tenantService";
import { tenantService } from "@/app/services/generated/tenantService";
import { reactive, shallowReadonly, watch } from "vue";
import userStore from "@/store/modules/userStore";
import {
  TenantSettings,
  tenantSettingsService,
} from "@/app/services/generated/tenantsettingsService";
import type { EnumPurchaseOrderDeliveryDisbursement } from "@/app/services/generated/enums/enumPurchaseOrderDeliveryDisbursement.generated";
import type { EnumCostPriceUpdateType } from "@/app/services/generated/enums/enumCostPriceUpdateType.generated";
import type { EnumEprProducerStatus } from "@/app/services/generated/enums/enumEprProducerStatus.generated";

interface TenantStoreState {
  companySettings: Readonly<CompanySettings>;
  bottleDepositSettings: Readonly<TenantDepositSettings>;
  deliveryNotificationSettings: Readonly<DeliveryNotificationSettings>;
  kegstarSettings: Readonly<KegstarSettings>;
  accountsLinkDetails: Readonly<AccountsLinkDetails>;
  tenantSettings: Readonly<TenantSettings>;
}

const state = reactive<TenantStoreState>({
  companySettings: new CompanySettings(),
  bottleDepositSettings: new TenantDepositSettings(),
  deliveryNotificationSettings: new DeliveryNotificationSettings(),
  kegstarSettings: new KegstarSettings(),
  accountsLinkDetails: new AccountsLinkDetails(),
  tenantSettings: new TenantSettings(),
});

async function refreshStore() {
  [
    state.companySettings,
    state.bottleDepositSettings,
    state.deliveryNotificationSettings,
    state.kegstarSettings,
    state.accountsLinkDetails,
    state.tenantSettings,
  ] = await Promise.all([
    companySettingsService.getCompanySettings(),
    bottleDepositSettingsService.getTenantSettings(),
    deliveryNotificationService.getDeliveryNotificationSettings(),
    kegstarLinkService.getKegstarSettings(),
    accountsLinkService.getAccountsLinkDetails(),
    tenantSettingsService.getTenantSettings(),
  ]);
}

// We should only need to refresh our store completely when the tenant as a whole has changed, otherwise our individual
// save calls should catch and individually replace.
watch(
  () => userStore.state.tenant,
  async (newTenant) => {
    if (newTenant) {
      await refreshStore();
    }
  },
  { immediate: true }
);

const saveDateAndTimeConfiguration = async (
  dateAndTimeConfiguration: DateAndTimeConfiguration
) => {
  await tenantService.updateDateAndTimeConfiguration(dateAndTimeConfiguration);

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveCurrencyConfiguration = async (
  currencyConfiguration: CurrencyConfiguration
) => {
  await tenantService.updateCurrencyConfiguration(currencyConfiguration);

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveNumberConfiguration = async (
  numberConfiguration: NumberConfiguration
) => {
  await tenantService.updateNumberConfiguration(numberConfiguration);

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveLanguageAndCultureConfiguration = async (
  languageAndCultureConfiguration: LanguageAndCultureConfiguration
) => {
  await tenantService.updateLanguageAndCultureConfiguration(
    languageAndCultureConfiguration
  );

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveCompanySettings = async (companySettings: CompanySettings) => {
  await companySettingsService.updateCompanySettings(companySettings);

  state.companySettings = await companySettingsService.getCompanySettings();
};

const saveTenantBottleDepositSettings = async (
  bottleDepositSettings: TenantDepositSettings
) => {
  await bottleDepositSettingsService.saveTenantSettings(bottleDepositSettings);

  state.bottleDepositSettings =
    await bottleDepositSettingsService.getTenantSettings();
};

const saveDeliveryNotificationSettings = async (
  deliveryNotificationSettings: DeliveryNotificationSettings
) => {
  await deliveryNotificationService.saveDeliveryNotificationSettings(
    deliveryNotificationSettings
  );

  state.deliveryNotificationSettings =
    await deliveryNotificationService.getDeliveryNotificationSettings();
};

const saveKegstarSettings = async (kegstarSettings: KegstarSettings) => {
  await kegstarLinkService.saveKegstarSettings(kegstarSettings);

  state.kegstarSettings = await kegstarLinkService.getKegstarSettings();
};

const setIsMegaBatchesEnabled = async (isMegaBatchesEnabled: boolean) => {
  await tenantSettingsService.setIsMegaBatchesEnabled(isMegaBatchesEnabled);

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const saveProductBatchDuration = async (productBatchDuration: number) => {
  await tenantSettingsService.setProductBatchDuration(productBatchDuration);

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const savePurchaseOrderSettings = async (
  poDefaultDeliveryDisbursement: EnumPurchaseOrderDeliveryDisbursement,
  poValueRequiringApproval: number | null
) => {
  await tenantSettingsService.savePurchaseOrderSettings(
    poDefaultDeliveryDisbursement,
    poValueRequiringApproval
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const saveProductionSettings = async (
  usesHopAlpha: boolean,
  costPriceUpdateType: EnumCostPriceUpdateType
) => {
  await tenantSettingsService.saveProductionSettings(
    usesHopAlpha,
    costPriceUpdateType
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const saveTaxSettings = async (usesRegionalTax: boolean) => {
  await tenantSettingsService.saveTaxSettings(usesRegionalTax);

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const saveSalesVisitSettings = async (salesVisitDefaultMinutes: number) => {
  await tenantSettingsService.saveSalesVisitSettings(salesVisitDefaultMinutes);

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const saveDisplaySettings = async (
  showTenantIndicator: boolean,
  tenantIndicatorColour: string,
  tenantIndicatorText: string
) => {
  await tenantSettingsService.saveDisplaySettings(
    showTenantIndicator,
    tenantIndicatorColour,
    tenantIndicatorText
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const saveEprSettings = async (producerStatus: EnumEprProducerStatus) => {
  await tenantSettingsService.saveEprSettings(producerStatus);

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

const saveInvoiceSettings = async (
  generateInvoiceNumberOnDispatchNoteCreation: boolean,
  emailInvoicesOnOrderCompletion: boolean
) => {
  await tenantSettingsService.saveInvoiceSettings(
    generateInvoiceNumberOnDispatchNoteCreation,
    emailInvoicesOnOrderCompletion
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings();
};

export default {
  // Using readonly breaks enums as it wraps with proxies.
  state: shallowReadonly(state),

  saveDateAndTimeConfiguration,
  saveCurrencyConfiguration,
  saveNumberConfiguration,
  saveCompanySettings,
  saveTenantBottleDepositSettings,
  saveDeliveryNotificationSettings,
  saveKegstarSettings,
  saveLanguageAndCultureConfiguration,
  setIsMegaBatchesEnabled,
  saveProductBatchDuration,
  savePurchaseOrderSettings,
  saveProductionSettings,
  saveTaxSettings,
  saveDisplaySettings,
  saveSalesVisitSettings,
  saveEprSettings,
  saveInvoiceSettings,
};
